import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'home',
        component: () => import('@/pages/Home.vue'),
        // Browse-first: visitante pode navegar sem login. Login só na hora de garantir oferta.
        meta: { title: 'Descobrir' },
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('@/pages/Login.vue'),
        meta: { guestOnly: true, title: 'Entrar' },
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('@/pages/Register.vue'),
        meta: { guestOnly: true, title: 'Criar conta' },
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('@/pages/ForgotPassword.vue'),
        meta: { guestOnly: true, title: 'Recuperar senha' },
    },
    {
        path: '/reset-password',
        name: 'reset-password',
        component: () => import('@/pages/ResetPassword.vue'),
        meta: { guestOnly: true, title: 'Nova senha' },
    },
    {
        path: '/offers/:id(\\d+)',
        name: 'offer-detail',
        component: () => import('@/pages/OfferDetail.vue'),
        meta: { title: 'Oferta' },
    },
    {
        path: '/establishments/:slug',
        name: 'establishment-detail',
        component: () => import('@/pages/EstablishmentDetail.vue'),
        meta: { title: 'Casa' },
    },
    {
        path: '/profile',
        name: 'profile',
        component: () => import('@/pages/Profile.vue'),
        meta: { requiresAuth: true, title: 'Perfil' },
    },
    {
        path: '/profile/notifications',
        name: 'notification-preferences',
        component: () => import('@/pages/NotificationPreferences.vue'),
        meta: { requiresAuth: true, title: 'Notificações' },
    },
    {
        path: '/favorites',
        name: 'favorites',
        component: () => import('@/pages/Favorites.vue'),
        meta: { requiresAuth: true, title: 'Favoritos' },
    },
    {
        path: '/coupons',
        name: 'coupons',
        component: () => import('@/pages/Coupons.vue'),
        meta: { requiresAuth: true, title: 'Meus cupons' },
    },
    {
        path: '/coupons/:id(\\d+)',
        name: 'coupon-detail',
        component: () => import('@/pages/CouponDetail.vue'),
        meta: { requiresAuth: true, title: 'Cupom' },
    },
    {
        path: '/live',
        name: 'live',
        component: () => import('@/pages/Live.vue'),
        meta: { title: 'Ao vivo' },
    },
    {
        path: '/live/:id(\\d+)',
        name: 'live-detail',
        component: () => import('@/pages/LiveDetail.vue'),
        meta: { title: 'Oferta ao vivo' },
    },
    {
        path: '/offline',
        name: 'offline',
        component: () => import('@/pages/Offline.vue'),
        meta: { title: 'Sem conexão' },
    },
    // Rotas antigas /v2/* redirecionam pra raiz — agora o visual moderno é o padrão.
    { path: '/v2', redirect: '/' },
    { path: '/v2/live', redirect: '/live' },
    { path: '/v2/coupons', redirect: '/coupons' },
    { path: '/v2/profile', redirect: '/profile' },
    { path: '/v2/offers/:id(\\d+)', redirect: (to) => `/offers/${to.params.id}` },
    { path: '/v2/live/:id(\\d+)', redirect: (to) => `/live/${to.params.id}` },
    { path: '/v2/coupons/:id(\\d+)', redirect: (to) => `/coupons/${to.params.id}` },

    // Backward-compat: /app/* legacy (PWA antigamente vivia em /app/). Redireciona
    // pro novo path na raiz. Ex: /app/live → /live, /app/ → /.
    {
        path: '/app/:pathMatch(.*)*',
        redirect: (to) => {
            const tail = Array.isArray(to.params.pathMatch) ? to.params.pathMatch.join('/') : (to.params.pathMatch ?? '');
            return tail === '' ? '/' : `/${tail}`;
        },
    },
    { path: '/app', redirect: '/' },

    { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
    // PWA agora vive na raiz (era /app/). Os assets seguem em /app/assets/* via vite base.
    history: createWebHistory('/'),
    routes,
    scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach((to) => {
    const auth = useAuthStore();

    if (to.meta.requiresAuth && !auth.isAuthenticated) {
        return { name: 'login', query: { redirect: to.fullPath } };
    }

    if (to.meta.guestOnly && auth.isAuthenticated) {
        return { name: 'home' };
    }

    return true;
});
